import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BaseEntity, BaseState } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { EnumPageDisplayModes, EnumPageEntitySources, EnumWindowSizes } from '@core/framework/sysenums.enum';
import { Html5Qrcode, Html5QrcodeScanner } from "html5-qrcode";
import { Html5QrcodeCameraScanConfig } from "html5-qrcode/esm/html5-qrcode";
import { Html5QrcodeResult, Html5QrcodeSupportedFormats } from "html5-qrcode/esm/core";
import { DxPopupComponent } from 'devextreme-angular';
import { TemplateRef } from '@angular/core';
import { LocalStorageService } from '@core/framework/local-storage.service';

class State extends BaseState {
    DeviceIDs: string[] = [];
    CurrentDeviceIndex = null;
    IsInititialized: boolean = false;
}

@Component({
    providers: ServiceBagServices,
    selector: 'm4u-barcodeboxdialog',
    templateUrl: './barcodeboxdialog.component.html',
    styleUrls: ['./barcodeboxdialog.component.scss']
})
export class BarcodeBoxDialogComponent extends BasePageComponent<BaseEntity, State>{
    @ViewChild('reader') ReaderElement: ElementRef | undefined;

    @Input() UseFrontCamera: boolean = false;

    private _lastUsedCameraStorageName: string = 'BarcodeBoxLastUsedCameraID';

    @Input() Config: any = {
        fps: 10,
        qrbox: this.QrBoxFunction,
        useBarCodeDetectorIfSupported: false,
        rememberLastUsedCamera: true,
        aspectRatio: 4 / 3,
        showTorchButtonIfSupported: true,
        formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODABAR,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.PDF_417,
            Html5QrcodeSupportedFormats.RSS_14,
            Html5QrcodeSupportedFormats.RSS_EXPANDED,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
        ],
        showZoomSliderIfSupported: true,
        defaultZoomValueIfSupported: 2
    };

    @Input() ExtraFields: TemplateRef<any>;
    @Output() OnTextDecoded: EventEmitter<string> = new EventEmitter<string>();
    @Output() OnResultDecoded: EventEmitter<Html5QrcodeResult> = new EventEmitter<Html5QrcodeResult>();
    html5QrCode!: Html5Qrcode;

    constructor(A: ServiceBagService,
        private localStorageService: LocalStorageService) {
        super(A, 'BarcodeBoxDialogComponent', BaseEntity, State, EnumPageDisplayModes.Dialog, EnumPageEntitySources.External);
    }

    protected override OnInitComponent() {
        this.State.IsInititialized = true;
    }

    protected override OnInitComponentFinished() {

        Html5Qrcode.getCameras().then(devices => {
            if (devices && devices.length) {
                this.State.DeviceIDs = devices.map(x => x.id);
                this.StartHtmlQrCode();
            }
        }).catch(err => {
            console.log(err);
        });

        //let html5QrcodeScanner = new Html5QrcodeScanner("reader", undefined, false);
        //html5QrcodeScanner.render(this.onScanSuccess, this.onScanFailure);
    }

    protected override OnDestroyComponent() {
        this.State.IsInititialized = false;
    }

    public override OnClose() {
        if (this.StopHtml5QrCode(false)) return false;
        this.CloseDialogWithCancel();
        return false;
    }

    private StartHtmlQrCode() {
        if (this.html5QrCode?.isScanning) {
            this.html5QrCode.stop().then((ignore) => {
                this.html5QrCode.start(
                    { deviceId: this.GetSavedDeviceID() },
                    this.Config,
                    this.QrCodeSuccessCallback,
                    (error) => { })
                    .catch((err) => {
                        console.log(err);
                    });
            }).catch((err) => {
                console.error(err);
            });
            return;
        }

        this.html5QrCode = new Html5Qrcode(this.ReaderElement?.nativeElement?.id);
        this.html5QrCode.start(
            { deviceId: this.GetSavedDeviceID() },
            this.Config,
            this.QrCodeSuccessCallback,
            (error) => { })
            .catch((err) => {
                console.log(err);
            });
    }

    private StopHtml5QrCode(isSuccess) {
        if (this.html5QrCode) {
            this.html5QrCode.stop().then((ignore) => {
                isSuccess ? this.CloseDialogWithSuccess() : this.CloseDialogWithCancel();
            }).catch((err) => {
                console.error(err);
                this.CloseDialogWithCancel();
            });
            return true;
        }
    }

    OnBarcodeClick (e) {
        this.State.CurrentDeviceIndex++;
        this.State.CurrentDeviceIndex = this.State.CurrentDeviceIndex % this.State.DeviceIDs.length;
        this.SaveDeviceID();
        this.StartHtmlQrCode();
    }

    QrCodeSuccessCallback = (decodedText: any, decodedResult: Html5QrcodeResult) => {
        this.OnTextDecoded.emit(decodedText);
        this.OnResultDecoded.emit(decodedResult);
    }

    CloseDialog(isSuccess: boolean) {
        if (this.StopHtml5QrCode(isSuccess)) return;
        this.CloseDialogWithCancel();
    }

    private GetSavedDeviceID() {
        const deviceID = this.localStorageService.GetItem(this._lastUsedCameraStorageName);
        const index = this.State.DeviceIDs.indexOf(deviceID);
        if (index < 0) {
            this.State.CurrentDeviceIndex = this.State.DeviceIDs.length - 1;
            return this.State.DeviceIDs[this.State.CurrentDeviceIndex];
        }

        this.State.CurrentDeviceIndex = index;
        return deviceID;
    }

    private SaveDeviceID() {
        this.localStorageService.SetItem(this._lastUsedCameraStorageName, this.State.DeviceIDs[this.State.CurrentDeviceIndex]);
    }

    private QrBoxFunction(viewfinderWidth, viewfinderHeight) {
        const minval = (val, min) => {
            if (val < min) {
                return min;
            }
            return val;
        }

        // Square QR Box, with size = 80% of the min edge.
        const minWidthThreshold = 250;
        const minHeightThreshold = 150;
        const edgeSizePercentage = 0.9;

        const minEdgeSize = (viewfinderWidth > viewfinderHeight) ?
            viewfinderHeight : viewfinderWidth;
        const qrboxWidth = Math.floor(minEdgeSize * edgeSizePercentage);
        const qrboxHeight = minval(Math.floor(qrboxWidth / 2), minHeightThreshold);
        if (qrboxWidth < minWidthThreshold) {
            let width = minval(qrboxWidth, minWidthThreshold);
            let height = minval(Math.floor(width / 2), minHeightThreshold);

            return {
                width: width,
                height: height
            };
        }
        return { width: qrboxWidth, height: qrboxHeight };
    }
}